import { RecentlyViewed } from '@theme/recently-viewed-products';

/** The view of the search template that answers with the rail's cards alone: templates/search.recently-viewed.json */
const CARDS_VIEW = 'recently-viewed';

/** What the search adds to a result's link so that a click counts for the search. These cards are not search results. */
const SEARCH_TRACKING_PARAMS = ['_pos', '_psq', '_sid', '_ss', '_v'];

/**
 * The rail of products the visitor looked at before this page (sections/recently-viewed-products.liquid).
 * The browser holds the list, so the cards are asked for once the rail comes near the screen.
 */
class RecentlyViewedProducts extends HTMLElement {
  /**
   * The observer that loads the cards when the rail is a screen away: the answer takes about half a second,
   * and cards that arrive in a rail already on screen are scored as a layout shift.
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadCards();
    },
    { rootMargin: '0px 0px 100% 0px' }
  );

  connectedCallback() {
    // The section's own script shows the rail before the first paint; this covers a rail that came in without it.
    this.hidden = this.#viewedProducts().length === 0;
    if (!this.hidden) this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }

  /**
   * The products to show: the most recent first, without the one this page is about.
   * @returns {string[]}
   */
  #viewedProducts() {
    const limit = Number(this.dataset.limit) || 10;

    try {
      return RecentlyViewed.getProducts()
        .filter((/** @type {string} */ id) => id !== this.dataset.productId)
        .slice(0, limit);
    } catch {
      return [];
    }
  }

  async #loadCards() {
    const url = new URL(this.dataset.url || '/search', location.origin);
    url.searchParams.set('q', this.#viewedProducts().map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', CARDS_VIEW);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = parsed.querySelector('.resource-list');
      const placeholder = this.querySelector('.resource-list');

      // None of the products is sold any more
      if (!cards?.querySelector('product-card') || !placeholder) {
        this.hidden = true;
        return;
      }

      for (const link of cards.querySelectorAll('a[href]')) {
        const href = new URL(link.getAttribute('href') || '', location.origin);
        for (const param of SEARCH_TRACKING_PARAMS) href.searchParams.delete(param);
        link.setAttribute('href', href.pathname + href.search + href.hash);
      }

      placeholder.replaceWith(cards);
    } catch (error) {
      console.error('Recently viewed products error:', error instanceof Error ? error.message : error);
      this.hidden = true;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
